import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";

const KINDS = [
  { key: "sport", title: "Sports", desc: "Filter buttons on the Shop page (Football, Baseball, etc.)" },
  { key: "attribute", title: "Card Attributes", desc: "Tags on cards (Autographs, Patches, Rookies, etc.)" },
  { key: "pack_label", title: "Mystery Pack Labels", desc: "Top-level pack tiers on the Mystery Packs page (GOLD LABEL, BLACK LABEL, WHITE LABEL)" },
];

export default function AdminFilters() {
  const [byKind, setByKind] = useState({ sport: [], attribute: [], pack_label: [], pack_sub: [] });
  const [expanded, setExpanded] = useState({ sport: true, attribute: false, pack_label: true });
  const [expandedSubs, setExpandedSubs] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/filters");
      const grouped = { sport: [], attribute: [], pack_label: [], pack_sub: [] };
      r.data.forEach((f) => { (grouped[f.kind] || (grouped[f.kind] = [])).push(f); });
      Object.keys(grouped).forEach((k) => grouped[k].sort((a, b) => a.sort_order - b.sort_order));
      setByKind(grouped);
    } catch { toast.error("Failed to load filters"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upsert = async (payload) => {
    try {
      await api.post("/admin/filters", payload);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this filter? Existing products keep the value but it won't appear as a filter option anymore.")) return;
    await api.delete(`/admin/filters/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <h1 className="font-display text-4xl mb-2">Shop Filters</h1>
      <p className="text-zinc-400 text-sm mb-8">
        Add, rename, reorder, or hide the filter buttons and dropdowns customers see on the Shop and Mystery Packs pages. Changes take effect immediately.
      </p>

      {KINDS.map(({ key, title, desc }) => (
        <Section
          key={key}
          sectionKey={key}
          title={title}
          desc={desc}
          expanded={expanded[key]}
          onToggle={() => setExpanded({ ...expanded, [key]: !expanded[key] })}
        >
          {expanded[key] && (
            <>
              <FilterList items={byKind[key] || []} onDelete={del} onEdit={upsert} kind={key} />
              <NewRow kind={key} onSubmit={upsert} nextOrder={((byKind[key] || []).length || 0) + 1} />
              {key === "pack_label" && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="text-label text-zinc-500 mb-4">Sub-Categories per label</div>
                  {(byKind.pack_label || []).map((lbl) => (
                    <SubSection
                      key={lbl.id}
                      label={lbl}
                      subs={(byKind.pack_sub || []).filter((s) => s.parent_key === lbl.key)}
                      expanded={!!expandedSubs[lbl.key]}
                      onToggle={() => setExpandedSubs({ ...expandedSubs, [lbl.key]: !expandedSubs[lbl.key] })}
                      onUpsert={upsert}
                      onDelete={del}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </Section>
      ))}
      <style>{`.filter-input { background: #000; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 8px 12px; color: white; font-size: 0.875rem; min-width: 0; }`}</style>
    </div>
  );
}

function Section({ sectionKey, title, desc, expanded, onToggle, children }) {
  return (
    <div className="border border-white/10 rounded p-6 bg-[var(--cl-surface)] mb-6">
      <button data-testid={`filter-section-${sectionKey}`} onClick={onToggle} className="flex items-center justify-between w-full text-left">
        <div>
          <h2 className="font-display text-2xl">{title}</h2>
          <p className="text-xs text-zinc-500 mt-1">{desc}</p>
        </div>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {expanded && <div className="mt-6">{children}</div>}
    </div>
  );
}

function FilterList({ items, onDelete, onEdit, kind }) {
  if (items.length === 0) return <div className="text-zinc-500 text-sm">Nothing yet — add one below.</div>;
  return (
    <div className="space-y-2" data-testid={`filter-list-${kind}`}>
      {items.map((f) => (
        <FilterRow key={f.id} f={f} onDelete={onDelete} onEdit={onEdit} />
      ))}
    </div>
  );
}

function FilterRow({ f, onDelete, onEdit }) {
  const [row, setRow] = useState(f);
  const [dirty, setDirty] = useState(false);
  const upd = (k, v) => { setRow({ ...row, [k]: v }); setDirty(true); };

  return (
    <div className="flex items-center gap-3 border border-white/10 rounded p-2 bg-black/40">
      <input value={row.label} onChange={(e) => upd("label", e.target.value)}
             className="filter-input flex-1 min-w-[200px]" data-testid={`filter-label-${f.id}`} placeholder="Display label" />
      <input value={row.key} onChange={(e) => upd("key", e.target.value)}
             className="filter-input w-40 flex-none" data-testid={`filter-key-${f.id}`} placeholder="key" />
      <input type="number" value={row.sort_order} onChange={(e) => upd("sort_order", parseInt(e.target.value, 10) || 0)}
             className="filter-input w-20 flex-none" data-testid={`filter-order-${f.id}`} />
      {row.color !== undefined && (
        <input type="color" value={row.color || "#FFFFFF"} onChange={(e) => upd("color", e.target.value)}
               className="w-10 h-10 rounded border border-white/10 bg-black cursor-pointer" data-testid={`filter-color-${f.id}`} />
      )}
      <label className="flex items-center gap-1 text-xs">
        <input type="checkbox" checked={row.active} onChange={(e) => upd("active", e.target.checked)}
               data-testid={`filter-active-${f.id}`} />
        Active
      </label>
      {dirty && (
        <button onClick={() => { onEdit(row); setDirty(false); }} className="btn-primary text-xs px-3 py-1"
                data-testid={`filter-save-${f.id}`}>Save</button>
      )}
      <button onClick={() => onDelete(f.id)} className="p-2 hover:text-[var(--cl-primary)]" data-testid={`filter-delete-${f.id}`}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function NewRow({ kind, onSubmit, nextOrder, parentKey }) {
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [color, setColor] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!label.trim() || !key.trim()) return;
    const payload = { kind, key: key.trim().toLowerCase().replace(/\s+/g, "_"),
                      label: label.trim(), sort_order: nextOrder, active: true };
    if (parentKey) payload.parent_key = parentKey;
    if (kind === "pack_label" && color) payload.color = color;
    onSubmit(payload);
    setLabel(""); setKey(""); setColor("");
  };

  return (
    <form onSubmit={submit} className="flex items-center gap-3 mt-3 pt-3 border-t border-white/5">
      <input value={label} onChange={(e) => { setLabel(e.target.value); if (!key) setKey(e.target.value.toLowerCase().replace(/\s+/g, "_")); }}
             placeholder="Display label (e.g. Hockey)" className="filter-input flex-1 min-w-[200px]"
             data-testid={`new-filter-label-${kind}${parentKey ? '-' + parentKey : ''}`} />
      <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="key (auto)"
             className="filter-input w-40 flex-none" data-testid={`new-filter-key-${kind}${parentKey ? '-' + parentKey : ''}`} />
      {kind === "pack_label" && (
        <input type="color" value={color || "#FFD700"} onChange={(e) => setColor(e.target.value)}
               className="w-10 h-10 rounded border border-white/10 bg-black cursor-pointer" />
      )}
      <button type="submit" className="btn-primary text-xs px-3 py-1" data-testid={`add-filter-${kind}${parentKey ? '-' + parentKey : ''}`}>
        <Plus size={14} className="inline -mt-0.5 mr-1" /> Add
      </button>
    </form>
  );
}

function SubSection({ label, subs, expanded, onToggle, onUpsert, onDelete }) {
  return (
    <div className="border border-white/5 rounded mb-3">
      <button data-testid={`filter-subsection-${label.key}`} onClick={onToggle} className="w-full flex items-center justify-between p-3 hover:bg-white/5">
        <span className="text-label" style={{ color: label.color || "#FFFFFF" }}>
          {label.label} · {subs.length} sub-categories
        </span>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {expanded && (
        <div className="p-3 border-t border-white/5">
          <FilterList items={subs} onDelete={onDelete} onEdit={onUpsert} kind={`pack_sub-${label.key}`} />
          <NewRow kind="pack_sub" parentKey={label.key} nextOrder={subs.length + 1} onSubmit={onUpsert} />
        </div>
      )}
    </div>
  );
}
